// eslint-disable-next-line import/no-extraneous-dependencies
import { useEffect, useState } from "react"
import { SetURLSearchParams } from "react-router-dom"

import {
    APPLICATION_ID_KW,
    ASSIGNED_TO,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    DNO_DEFAULT_ORDER_BY,
    INSTALLER_DATE_FROM_KW,
    INSTALLER_DATE_TO_KW,
    ORDER_BY_TIMESTAMP,
    READY_FOR_CX,
    STATUS,
} from "../constants/generalConstants"
import useDnoUserSuggestion from "../services/middleware/hooks/dnoInterface/protected/useDnoUserSuggestion"

/**
 * Hook for deriving and managing filter panel input values from URL search
 * params for dno application search page. database search API.
 * Search params are treated as the filter panel state. This hook abstracts the
 * translation of this state into objects
 * the filter panel inputs can read.
 *
 * @param searchParams - URL search parameters
 * @param setSearchParams - setter function for URL search parameters
 * @returns - helper function for setting a filter value and values for all
 * database filter panel options.
 */
export const useApplicationDeriveFilters = (
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams,
    isDnoMode: boolean
): {
    applicationId: string
    clearAllFilters: () => void
    setFilterFromTextfield: (newValue: string, searchParam: string) => void
    orderBy: string
    orderDirection: string
    dateFrom: string
    dateTo: string
    status: string | null
    assignedTo: string
    assignedToName: string
    readyForCx: boolean
    setOrderBy: (orderBy: string, orderDirection: string) => void
} => {
    /**
     * Updates URL search params for filter panel elements which are text
     * @param newValue - new value being set
     * @param searchParam - url search param for the textfield
     */
    const setFilterFromTextfield = (
        newValue: string,
        searchParam: string
    ): void => {
        if (newValue) {
            searchParams.set(searchParam, String(newValue))
        } else {
            searchParams.delete(searchParam)
        }
        setSearchParams(searchParams)
    }

    /**
     * Sets order by and order direction URL params
     * @param orderBy - column to be sorted on
     * @param orderDirection - sort direction (asc | desc)
     */
    const setOrderBy = (orderBy: string, orderDirection: string): void => {
        searchParams.set(orderBy, orderDirection)
        setSearchParams(searchParams)
    }

    // Filter params
    const applicationId = searchParams.get(APPLICATION_ID_KW) || ""
    const dateFrom =
        searchParams.get(
            isDnoMode ? DNO_DATE_FROM_KW : INSTALLER_DATE_FROM_KW
        ) || ""
    const dateTo =
        searchParams.get(isDnoMode ? DNO_DATE_TO_KW : INSTALLER_DATE_TO_KW) ||
        ""
    const orderBy = ORDER_BY_TIMESTAMP
    const orderDirection =
        searchParams.get(ORDER_BY_TIMESTAMP) || DNO_DEFAULT_ORDER_BY
    const status = searchParams.get(STATUS) || null
    const readyForCx = searchParams.get(READY_FOR_CX) === "true"
    const assignedTo = searchParams.get(isDnoMode ? ASSIGNED_TO : "") || ""

    const [assignedToName, setAssignedToName] = useState<string>("")

    const { getUserNameFromId } = useDnoUserSuggestion("", isDnoMode)
    useEffect(() => {
        const name = getUserNameFromId(assignedTo)
        setAssignedToName(name)
    }, [assignedTo, getUserNameFromId])

    const clearAllFilters = (): void => {
        setSearchParams(new URLSearchParams())
    }

    return {
        applicationId,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        status,
        assignedTo,
        assignedToName,
        readyForCx,
        setOrderBy,
        setFilterFromTextfield,
        clearAllFilters,
    }
}
